import React, { useState } from 'react'
import { Alert, Button, Modal, Tag, Upload } from 'antd'
import type { RcFile } from 'antd/lib/upload/interface'
import { InboxOutlined, CheckCircleFilled } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '~/Store'
import {
  getImportResult,
  getImportTarget,
  getIsImporting,
  ProductActions,
} from '~/Store/Products'
import styles from './Products.module.less'

const ALLOWED_EXTENSION = '.xlsx'

export const ImportPermissionsModal = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const target = useAppSelector(getImportTarget)
  const isImporting = useAppSelector(getIsImporting)
  const result = useAppSelector(getImportResult)

  const [file, setFile] = useState<RcFile | undefined>(undefined)
  const [fileError, setFileError] = useState<string | undefined>(undefined)

  if (!target) return null

  const close = () => {
    dispatch(ProductActions.closeImportModal())
    setFile(undefined)
    setFileError(undefined)
  }

  const resetForAnotherFile = () => {
    dispatch(ProductActions.openImportModal(target))
    setFile(undefined)
    setFileError(undefined)
  }

  const beforeUpload = (candidate: RcFile) => {
    const isXlsx = candidate.name.toLowerCase().endsWith(ALLOWED_EXTENSION)
    if (!isXlsx) {
      setFileError(t('admin.invalidExcelExtension'))
      return Upload.LIST_IGNORE
    }
    if (candidate.size === 0) {
      setFileError(t('admin.emptyFile'))
      return Upload.LIST_IGNORE
    }
    setFileError(undefined)
    setFile(candidate)
    return false // không auto-upload — backend nhận file lúc bấm Import
  }

  const removeFile = () => {
    setFile(undefined)
    setFileError(undefined)
  }

  const onImport = () => {
    if (!file) {
      setFileError(t('admin.pleaseChooseFile'))
      return
    }
    dispatch(ProductActions.importPermissions({ productId: target.guid, file }))
  }

  const renderUploadForm = () => (
    <>
      <div className={styles.ImportTarget}>
        <div className={styles.Label}>{t('admin.product')}</div>
        <div className={styles.Value}>{target.name}</div>
        <div className={styles.Label} style={{ marginTop: 8 }}>
          {t('admin.clientId')}
        </div>
        <div className={styles.Value}>{target.clientId}</div>
      </div>

      <div className={styles.SupportedData}>
        <span>{t('admin.supportedData')}:</span>
        <ul>
          <li>{t('admin.permissionGroups')}</li>
          <li>{t('admin.permissions')}</li>
          <li>{t('admin.roles')}</li>
        </ul>
      </div>

      <Upload.Dragger
        accept={ALLOWED_EXTENSION}
        maxCount={1}
        fileList={file ? [file] : []}
        beforeUpload={beforeUpload}
        onRemove={removeFile}
        disabled={isImporting}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">{t('admin.chooseExcelFile')}</p>
      </Upload.Dragger>
      {fileError && (
        <Alert
          style={{ marginTop: 12 }}
          type="error"
          showIcon
          message={fileError}
        />
      )}
    </>
  )

  const renderSheetTags = (sheets: string[], color: string, icon?: React.ReactNode) => {
    if (sheets.length === 0) return <span>-</span>
    return (
      <>
        {sheets.map(sheet => (
          <Tag color={color} key={sheet} className={styles.SheetTag} icon={icon}>
            {sheet}
          </Tag>
        ))}
      </>
    )
  }

  const renderCountRow = (label: string, value: number) => (
    <div className={styles.CountRow} key={label}>
      <span>{label}</span>
      <b>{value}</b>
    </div>
  )

  const renderResult = () => {
    if (!result) return null
    return (
      <div className={styles.ResultSummary}>
        <Alert
          style={{ marginBottom: 16 }}
          type={result.success ? 'success' : 'error'}
          showIcon
          message={
            result.success
              ? t('admin.importCompleted')
              : t('admin.importFailed')
          }
        />

        <div className={styles.SheetGroup}>
          <div className={styles.SheetTitle}>{t('admin.processedSheets')}</div>
          {renderSheetTags(result.processedSheets, 'green', <CheckCircleFilled />)}
        </div>

        {result.unsupportedSheets.length > 0 && (
          <div className={styles.SheetGroup}>
            <div className={styles.SheetTitle}>{t('admin.unsupportedSheets')}</div>
            {renderSheetTags(result.unsupportedSheets, 'default')}
          </div>
        )}

        <div className={styles.SheetGroup}>
          <div className={styles.SheetTitle}>{t('admin.permissionGroups')}</div>
          {renderCountRow(t('admin.created'), result.permissionGroups.created)}
          {renderCountRow(t('admin.updated'), result.permissionGroups.updated)}
          {renderCountRow(t('admin.unchanged'), result.permissionGroups.unchanged)}
        </div>

        <div className={styles.SheetGroup}>
          <div className={styles.SheetTitle}>{t('admin.permissions')}</div>
          {renderCountRow(t('admin.created'), result.permissions.created)}
          {renderCountRow(t('admin.updated'), result.permissions.updated)}
          {renderCountRow(t('admin.unchanged'), result.permissions.unchanged)}
          {renderCountRow(t('admin.skipped'), result.permissions.skipped)}
        </div>

        <div className={styles.SheetGroup}>
          <div className={styles.SheetTitle}>{t('admin.roles')}</div>
          {renderCountRow(t('admin.created'), result.roles.created)}
          {renderCountRow(t('admin.updated'), result.roles.updated)}
          {renderCountRow(t('admin.unchanged'), result.roles.unchanged)}
          {renderCountRow(t('admin.skipped'), result.roles.skipped)}
        </div>

        {result.errors.length > 0 && (
          <div className={styles.SheetGroup}>
            <div className={styles.SheetTitle}>{t('admin.importErrors')}</div>
            <div className={styles.ErrorList}>
              {result.errors.map((err, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <Alert
                  key={idx}
                  type="error"
                  style={{ marginBottom: 6 }}
                  message={
                    err.sheet
                      ? `[${err.sheet}${err.row ? ` #${err.row}` : ''}${
                          err.column ? ` - ${err.column}` : ''
                        }] ${err.message}`
                      : err.message
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const footer = result
    ? [
        !result.success && (
          <Button key="retry" onClick={resetForAnotherFile}>
            {t('admin.chooseAnotherFile')}
          </Button>
        ),
        <Button key="close" type="primary" onClick={close}>
          {t('admin.close')}
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={close} disabled={isImporting}>
          {t('cancel')}
        </Button>,
        <Button key="import" type="primary" loading={isImporting} onClick={onImport}>
          {isImporting ? t('admin.importing') : t('admin.import')}
        </Button>,
      ]

  return (
    <Modal
      visible
      title={t('admin.importPermissionCatalog')}
      onCancel={close}
      footer={footer}
      maskClosable={!isImporting}
      closable={!isImporting}
      width={560}
    >
      {result ? renderResult() : renderUploadForm()}
    </Modal>
  )
}
